import React from "react";
import { Alert, Box, Button, Container, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePanelDashboard } from "../hooks/usePanelDashboard";
import { useImportFileReviewController } from "../hooks/panelDashboard/useImportFileReviewController";
import { CARE_SOURCE_REVIEW_POLICIES, CARE_SOURCE_TYPES, CARE_SOURCE_USE_CASES, organizeCapturedCareSource } from "../features/careSourceProcessing";
import ImportHistoryReviewSheet from "../features/careSourceProcessing/ImportHistoryReviewSheet";
import ImportHistoryBatchHistory from "../features/importHistory/ImportHistoryBatchHistory";
import { completeImportHistoryBatch, findActiveImportHistoryBatch, findPreviouslyUploadedImportFiles, resumeImportHistoryBatch, saveImportHistoryBatchForLater, subscribeToImportHistoryBatch, subscribeToImportHistoryBatches, subscribeToImportHistorySources, updateImportHistoryBatch } from "../features/importHistory/importHistoryBatchService";
import { deriveImportBatchProgress, MAX_IMPORT_HISTORY_FILES } from "../features/importHistory/importHistoryBatchModel";
import colors from "../assets/theme/colors";

const IMPORT_HISTORY_ACCEPTED_FILES = ".pdf,.txt,.xlsx,.docx,.png,.jpg,.jpeg,image/png,image/jpeg";

const ImportHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hook = usePanelDashboard({ activeChildOnly: true });
  const requestedChildId = location.state?.selectedChildId || "";
  const child = hook.children.find((item) => item.id === requestedChildId) || hook.children.find((item) => item.id === hook.currentChildId) || hook.children[0] || null;
  const role = hook.getUserRoleForChild?.(child?.id) || "";
  const canManage = ["care_owner", "care_partner"].includes(role);
  const inputRef = React.useRef(null);
  const [batch, setBatch] = React.useState(null);
  const [batches, setBatches] = React.useState([]);
  const [batchLoading, setBatchLoading] = React.useState(true);
  const [sources, setSources] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const activeBatchLoadVersion = React.useRef(0);

  const loadActiveBatch = React.useCallback(async () => {
    const loadVersion = activeBatchLoadVersion.current + 1;
    activeBatchLoadVersion.current = loadVersion;
    if (!child?.id || !canManage) {
      setBatch(null);
      setBatchLoading(false);
      return;
    }
    try {
      const activeBatch = await findActiveImportHistoryBatch({ childId: child.id });
      if (activeBatchLoadVersion.current === loadVersion) setBatch(activeBatch);
    } catch (loadError) {
      if (activeBatchLoadVersion.current === loadVersion) setError(loadError?.message || "We couldn't load this import.");
    } finally {
      if (activeBatchLoadVersion.current === loadVersion) setBatchLoading(false);
    }
  }, [canManage, child?.id]);

  React.useEffect(() => {
    setBatch(null);
    setSources([]);
    setPendingFiles([]);
    setReviewOpen(false);
    setBatchLoading(true);
    loadActiveBatch();
    return () => {
      activeBatchLoadVersion.current += 1;
    };
  }, [loadActiveBatch]);
  React.useEffect(() => {
    if (!child?.id || !canManage) return undefined;
    return subscribeToImportHistoryBatches({
      childId: child.id,
      onBatches: setBatches,
      onError: setError,
    });
  }, [canManage, child?.id]);
  React.useEffect(() => {
    if (!batch?.id) return undefined;
    return subscribeToImportHistoryBatch({
      batchId: batch.id,
      onBatch: setBatch,
      onError: setError,
    });
  }, [batch?.id]);
  React.useEffect(() => {
    const ids = (batch?.items || []).map((item) => item.sourceNoteId).filter(Boolean);
    return subscribeToImportHistorySources({
      sourceNoteIds: ids,
      onSources: setSources,
      onError: setError,
    });
  }, [batch?.items]);

  const { handleImportFileChange } = useImportFileReviewController({
    children: child ? [child] : [],
    currentChildId: child?.id || "",
    closeImportStartModal: () => {},
    startImportLoading: () => setBusy(true),
    stopImportLoading: () => setBusy(false),
    clearImportError: () => setError(""),
    setImportError: setError,
    setImportStatus: (message) => {
      setStatus(message);
      if (message.startsWith("Import batch saved")) loadActiveBatch();
    },
    selectedSourceType: CARE_SOURCE_TYPES.FILE_IMPORT,
    sourceUseCase: CARE_SOURCE_USE_CASES.IMPORT_HISTORY,
    reviewPolicy: CARE_SOURCE_REVIEW_POLICIES.CAREGIVER_APPROVAL_REQUIRED,
    user: hook.user,
    importBatch: batch,
  });

  const progress = deriveImportBatchProgress({ batch: batch || {}, sources });
  const failedUploadCount = progress.items.filter((item) => item.uploadStatus === "failed").length;
  React.useEffect(() => {
    if (!batch?.id || batch.active === false || !progress.totalCount || batch.status === progress.status) return;
    updateImportHistoryBatch(batch.id, { status: progress.status }).catch(() => {});
  }, [batch?.active, batch?.id, batch?.status, progress.status, progress.totalCount]);
  const retryFailed = async () => {
    const failedSources = progress.items.map((item) => item.source).filter((source) => source && ["failed", "error"].includes(source.status || source.processingStatus));
    setBusy(true);
    setError("");
    try {
      const results = await Promise.allSettled(failedSources.map((source) => organizeCapturedCareSource({ sourceNoteId: source.id, options: { retry: true } })));
      if (results.some((result) => result.status === "rejected")) {
        setError("Some failed documents could not be reprocessed. You can retry them again.");
      }
    } finally {
      setBusy(false);
    }
  };
  const retryIncomplete = async () => {
    const sourceIds = progress.recoveryItems.map((item) => item.source?.id).filter(Boolean);
    setBusy(true);
    setError("");
    setStatus("Reprocessing preserved documents for Import History review.");
    try {
      const results = await Promise.allSettled(
        sourceIds.map((sourceNoteId) => organizeCapturedCareSource({
          sourceNoteId,
          options: { retry: true },
        })),
      );
      if (results.some((result) => result.status === "rejected")) {
        setError("Some documents could not be reprocessed. You can retry them again.");
      }
    } finally {
      setBusy(false);
    }
  };
  const handleFileSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (batch) {
      await handleImportFileChange({ target: { files, value: "retry" } });
      return;
    }
    if (files.length > MAX_IMPORT_HISTORY_FILES) {
      setError(`Choose no more than ${MAX_IMPORT_HISTORY_FILES} files at a time.`);
      return;
    }
    const previouslyUploadedIndexes = await findPreviouslyUploadedImportFiles({ childId: child.id, files });
    const seen = new Set();
    setPendingFiles(
      files.map((file, index) => {
        const identity = `${file.name}|${file.type}|${file.size}|${file.lastModified || 0}`;
        const previouslyUploaded = previouslyUploadedIndexes.has(index);
        const duplicate = seen.has(identity) || previouslyUploaded;
        seen.add(identity);
        return { file, index, duplicate, previouslyUploaded, include: !duplicate };
      }),
    );
  };
  const startImport = async () => {
    const files = pendingFiles.filter((item) => item.include).map((item) => item.file);
    if (!files.length) return;
    await handleImportFileChange({ target: { files, value: "pending" } });
    setPendingFiles([]);
  };
  const clearCurrentBatch = React.useCallback(() => {
    setBatch(null);
    setSources([]);
    setReviewOpen(false);
  }, []);
  const saveForLater = async ({ goHome = false } = {}) => {
    if (busy) return false;
    setBusy(true);
    setError("");
    try {
      if (batch?.id) await saveImportHistoryBatchForLater(batch.id);
      clearCurrentBatch();
      setStatus("Your import is saved. You can continue reviewing it any time.");
      if (goHome) navigate("/home", { replace: true });
      return true;
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save this import for later. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const importMore = async () => {
    if (await saveForLater()) setStatus("Your earlier import is saved. Choose up to 10 more documents.");
  };
  const resumeBatch = async (savedBatch) => {
    if (busy) return;
    if (savedBatch?.childId && savedBatch.childId !== child?.id) {
      setError("This import belongs to a different care profile.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (batch?.id && batch.id !== savedBatch.id) await saveImportHistoryBatchForLater(batch.id);
      await Promise.all(
        batches
          .filter((item) => item.active === true && item.id !== savedBatch.id && item.id !== batch?.id)
          .map((item) => saveImportHistoryBatchForLater(item.id)),
      );
      await resumeImportHistoryBatch(savedBatch.id);
      setSources([]);
      setBatch({ ...savedBatch, active: true });
      setStatus("Import reopened. Your earlier review work is still saved.");
    } catch (resumeError) {
      setError(resumeError?.message || "We couldn't reopen this import.");
    } finally {
      setBusy(false);
    }
  };
  const finishReview = async (result) => {
    if (!result?.complete) {
      setReviewOpen(false);
      return;
    }
    await completeImportHistoryBatch(batch.id);
    setReviewOpen(false);
    navigate("/home", { replace: true });
  };
  const finishEmptyImport = async () => {
    if (!batch?.id || busy) return;
    setBusy(true);
    setError("");
    try {
      await completeImportHistoryBatch(batch.id);
      clearCurrentBatch();
      setStatus("Import completed. The original documents remain in Documents.");
    } catch (finishError) {
      setError(finishError?.message || "We couldn't finish this import.");
    } finally {
      setBusy(false);
    }
  };

  if (hook.loading) return <Container sx={{ py: 4 }}>Loading…</Container>;
  if (!canManage)
    return (
      <Container maxWidth="sm" sx={{ py: 5 }}>
        <Alert severity="warning">Only Care Owners and Care Partners can import care history.</Alert>
      </Container>
    );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.semantic.page, py: 3 }}>
      <Container maxWidth="md">
        <Stack spacing={2}>
          <Box>
            <Button onClick={() => navigate("/home")} sx={{ px: 0 }}>
              ← Home
            </Button>
            <Typography
              component="h1"
              sx={{
                fontSize: "1.7rem",
                fontWeight: 900,
                color: colors.semantic.ink,
              }}
            >
              Import care history
            </Typography>
            <Typography color="text.secondary">Upload up to {MAX_IMPORT_HISTORY_FILES} documents. Every original is preserved and nothing is added until you review it.</Typography>
          </Box>
          {error ? <Alert severity="warning">{String(error?.message || error)}</Alert> : null}
          {status ? <Alert severity="info">{status}</Alert> : null}
          {batchLoading ? (
            <LinearProgress aria-label="Loading import history" />
          ) : !batch ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Choose documents containing past care entries</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                PDF, DOCX, TXT, XLSX, PNG or JPG. Up to 20 MB per file.
              </Typography>
              {!pendingFiles.length ? (
                <Button variant="contained" disabled={busy} onClick={() => inputRef.current?.click()}>
                  Choose files
                </Button>
              ) : (
                <Stack spacing={1} sx={{ textAlign: "left" }}>
                  {pendingFiles.map((item) => (
                    <Stack key={`${item.index}-${item.file.name}`} direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography>{item.file.name}</Typography>
                        {item.duplicate ? (
                          <Typography color="warning.main" variant="caption">
                            {item.previouslyUploaded ? "Previously uploaded — excluded by default" : "Duplicate selection — excluded by default"}
                          </Typography>
                        ) : null}
                      </Box>
                      {item.duplicate ? (
                        <Button size="small" onClick={() => setPendingFiles((current) => current.map((entry) => (entry.index === item.index ? { ...entry, include: !entry.include } : entry)))}>
                          {item.include ? "Exclude" : "Import anyway"}
                        </Button>
                      ) : null}
                    </Stack>
                  ))}
                  <Button variant="contained" disabled={busy || !pendingFiles.some((item) => item.include)} onClick={startImport}>
                    Start import
                  </Button>
                </Stack>
              )}
              <input ref={inputRef} aria-label="Choose import history files" hidden multiple type="file" accept={IMPORT_HISTORY_ACCEPTED_FILES} onChange={handleFileSelection} />
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>
                    {progress.recoveryCount ? "Import needs to be reprocessed" : progress.isComplete ? "Import ready" : "Organizing documents"}
                  </Typography>
                  <Typography color="text.secondary">
                    {progress.recoveryCount
                      ? `${progress.recoveryCount} of ${progress.totalCount} documents need reprocessing`
                      : `${progress.completedCount} of ${progress.totalCount} documents finished`}
                  </Typography>
                  <LinearProgress variant="determinate" value={progress.totalCount ? (progress.completedCount / progress.totalCount) * 100 : 0} sx={{ mt: 1 }} />
                </Box>
                {progress.items.map((item) => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.fileName}</Typography>
                    <Typography color={item.uploadStatus === "failed" || ["failed", "error"].includes(item.source?.status) ? "error" : "text.secondary"}>
                      {item.uploadStatus === "failed"
                        ? "Upload failed"
                        : progress.recoveryItems.some((recoveryItem) => recoveryItem.key === item.key)
                          ? "Needs reprocessing"
                          : item.source?.status === "needs_review"
                            ? `${item.source.pendingTimelineReview?.candidateCount || 0} found`
                            : item.source?.status === "saved_source"
                              ? "No historical entries found"
                              : item.source?.status || item.uploadStatus}
                    </Typography>
                  </Stack>
                ))}
                {progress.recoveryCount ? (
                  <Button variant="contained" onClick={retryIncomplete} disabled={busy}>
                    Reprocess {progress.recoveryCount} preserved documents
                  </Button>
                ) : null}
                {progress.isComplete && progress.reviewCount > 0 ? (
                  <Button
                    variant="contained"
                    onClick={() => setReviewOpen(true)}
                    sx={{ bgcolor: colors.semantic.sageAction, color: colors.semantic.sageActionInk, fontWeight: 900, "&:hover": { bgcolor: colors.semantic.sageActionHover } }}
                  >
                    Review {progress.reviewCount} imported entries
                  </Button>
                ) : null}
                {progress.isComplete && progress.reviewCount === 0 && !progress.failedCount ? (
                  <Button variant="contained" onClick={finishEmptyImport} disabled={busy} sx={{ fontWeight: 900 }}>
                    Finish import
                  </Button>
                ) : null}
                {failedUploadCount ? (
                  <Box>
                    <Typography sx={{ mb: 0.7, fontSize: "0.78rem", color: "text.secondary", textAlign: "center" }}>
                      The failed original was not uploaded. Choose that same file again to retry it.
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => inputRef.current?.click()}
                      disabled={busy}
                      sx={{ bgcolor: "#FFFFFF", borderColor: colors.semantic.sageDeep, color: colors.semantic.sageActionInk, fontWeight: 900 }}
                    >
                      Choose failed {failedUploadCount === 1 ? "file" : "files"} again
                    </Button>
                  </Box>
                ) : null}
                {progress.items.some((item) => item.source && ["failed", "error"].includes(item.source.status || item.source.processingStatus)) ? (
                  <Button onClick={retryFailed} disabled={busy}>
                    Retry processing failed documents
                  </Button>
                ) : null}
                <Button variant="outlined" onClick={importMore} disabled={busy} sx={{ fontWeight: 850 }}>
                  Import more documents
                </Button>
                <Button color="inherit" onClick={() => saveForLater({ goHome: true })} disabled={busy}>
                  Save for later and return Home
                </Button>
                <input ref={inputRef} aria-label="Choose import history files" hidden multiple type="file" accept={IMPORT_HISTORY_ACCEPTED_FILES} onChange={handleFileSelection} />
              </Stack>
            </Paper>
          )}
          <ImportHistoryBatchHistory batches={batches.filter((item) => item.id !== batch?.id)} onResumeBatch={resumeBatch} resumeDisabled={busy} />
        </Stack>
      </Container>
      <ImportHistoryReviewSheet sources={progress.items.map((item) => item.source).filter((source) => source?.pendingTimelineReview?.candidates?.length)} existingMedications={child?.medicationDetails || []} open={reviewOpen} onClose={finishReview} />
    </Box>
  );
};

export default ImportHistoryPage;
